import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";

import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Onboarding from "@/pages/Onboarding";

import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import RefundPolicy from "@/pages/RefundPolicy";
import AiTransparency from "@/pages/AiTransparency";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import { Blog, BlogPost } from "@/pages/Blog";

import UseCases from "@/pages/use-cases/UseCases";
import Healthcare from "@/pages/use-cases/Healthcare";
import Ecommerce from "@/pages/use-cases/Ecommerce";
import RealEstate from "@/pages/use-cases/RealEstate";
import Education from "@/pages/use-cases/Education";
import Restaurants from "@/pages/use-cases/Restaurants";
import LawFirms from "@/pages/use-cases/LawFirms";

import DashboardLayout from "@/layouts/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import AgentList from "@/pages/agents/AgentList";
import AgentBuilder from "@/pages/agents/AgentBuilder";
import Conversations from "@/pages/conversations/Conversations";
import VoiceTester from "@/pages/voice/VoiceTester";
import CallsPage from "@/pages/calls/CallsPage";
import AssistantsPage from "@/pages/calls/AssistantsPage";
import PhoneNumbersPage from "@/pages/phone-numbers/PhoneNumbersPage";
import CRM from "@/pages/crm/CRM";
import Appointments from "@/pages/appointments/Appointments";
import Billing from "@/pages/billing/Billing";
import Analytics from "@/pages/analytics/Analytics";
import Settings from "@/pages/settings/Settings";

import AdminLayout from "@/layouts/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminTenants from "@/pages/admin/AdminTenants";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminUsage from "@/pages/admin/AdminUsage";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminKnowledgeBase from "@/pages/admin/AdminKnowledgeBase";
import AdminEmails from "@/pages/admin/AdminEmails";

import EmbedWidget from "@/pages/widget/EmbedWidget";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Hard-gate: vendor users MUST verify their email before reaching the dashboard
  if (user.role === "vendor_admin" && !user.email_verified) return <Navigate to="/verify-email" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function OnboardingGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "vendor_admin" && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-right" />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/widget/:agentId" element={<EmbedWidget />} />

            {/* Marketing / company pages */}
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/ai-transparency" element={<AiTransparency />} />

            {/* Use-case (industry) pages */}
            <Route path="/use-cases" element={<UseCases />} />
            <Route path="/use-cases/healthcare" element={<Healthcare />} />
            <Route path="/use-cases/ecommerce" element={<Ecommerce />} />
            <Route path="/use-cases/real-estate" element={<RealEstate />} />
            <Route path="/use-cases/education" element={<Education />} />
            <Route path="/use-cases/restaurants" element={<Restaurants />} />
            <Route path="/use-cases/law-firms" element={<LawFirms />} />

            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />

            <Route path="/dashboard" element={<Protected><OnboardingGate><DashboardLayout /></OnboardingGate></Protected>}>
              <Route index element={<Overview />} />
              <Route path="agents" element={<AgentList />} />
              <Route path="agents/:agentId" element={<AgentBuilder />} />
              <Route path="conversations" element={<Conversations />} />
              <Route path="voice" element={<VoiceTester />} />
              <Route path="calls" element={<Navigate to="/dashboard/calls/outbound" replace />} />
              <Route path="calls/outbound" element={<CallsPage />} />
              <Route path="calls/assistants" element={<AssistantsPage />} />
              <Route path="phone-numbers" element={<PhoneNumbersPage />} />
              <Route path="crm" element={<CRM />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="billing" element={<Billing />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="/admin" element={<AdminOnly><AdminLayout /></AdminOnly>}>
              <Route index element={<AdminOverview />} />
              <Route path="tenants" element={<AdminTenants />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="packages" element={<AdminPackages />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="usage" element={<AdminUsage />} />
              <Route path="knowledge-base" element={<AdminKnowledgeBase />} />
              <Route path="emails" element={<AdminEmails />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
